import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Drawer, Box, Typography, Stack, IconButton, Chip, Tabs, Tab, Paper, Grid,
  Button, Tooltip, Skeleton, Alert,
} from '@mui/material';
import { Close, ContentCopy, People, Payments, CardGiftcard, PersonAdd } from '@mui/icons-material';
import SerialNoDataGrid from '../ui/SerialNoDataGrid';
import { GridColDef } from '@mui/x-data-grid';
import { getPartnerReferralSummary, type PartnerRow } from '../../services/partnerUserService';
import { formatMoney } from '../../utils/currency';
import { formatRegisteredDate } from '../../utils/dateFormat';

interface PartnerReferralDrawerProps {
  open: boolean;
  onClose: () => void;
  partner: PartnerRow | null;
}

function StatCard({
  title, value, icon, color,
}: {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%', borderRadius: 2 }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ p: 1, borderRadius: 1.5, bgcolor: `${color}18`, color, display: 'flex' }}>
          {icon}
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">{title}</Typography>
          <Typography variant="h6" fontWeight={800} lineHeight={1.2}>{value}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function PartnerReferralDrawer({ open, onClose, partner }: PartnerReferralDrawerProps) {
  const [tab, setTab] = useState(0);
  const [copied, setCopied] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['partner-referrals-summary', partner?._id],
    queryFn: () => getPartnerReferralSummary(partner!._id),
    enabled: open && !!partner?._id,
  });

  const handleCopy = async () => {
    const code = data?.referralCode || partner?.referralCode;
    if (!code) return;
    await navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const referredColumns: GridColDef[] = [
    {
      field: 'name', headerName: 'Referred user', flex: 1, minWidth: 140,
      valueGetter: (_v, row) => `${(row as { firstName: string }).firstName} ${(row as { lastName?: string }).lastName || ''}`.trim(),
    },
    {
      field: 'email', headerName: 'Email', width: 160,
      valueGetter: (_v, row) => String((row as { email?: string }).email || '—'),
    },
    {
      field: 'phone', headerName: 'Phone', width: 130,
      valueGetter: (_v, row) => String((row as { phone?: string }).phone || '—'),
    },
    {
      field: 'createdAt', headerName: 'Joined', width: 110,
      valueFormatter: (v) => formatRegisteredDate(v as string),
    },
    {
      field: 'bonusAmount', headerName: 'Bonus', width: 80,
      valueFormatter: (v) => `${formatMoney(v ?? 0)}`,
    },
  ];

  const historyColumns: GridColDef[] = [
    { field: 'description', headerName: 'Event', flex: 1 },
    {
      field: 'amount', headerName: 'Amount', width: 90,
      valueFormatter: (v) => `${formatMoney(v ?? 0)}`,
    },
    {
      field: 'referredUserName', headerName: 'User', width: 140,
    },
    {
      field: 'createdAt', headerName: 'Date', width: 110,
      valueFormatter: (v) => formatRegisteredDate(v as string),
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 520, md: 640 } } }}
    >
      <Box sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="overline" color="text.secondary">Referral program</Typography>
            <Typography variant="h6" fontWeight={800}>
              {partner?.firstName} {partner?.lastName}
            </Typography>
          </Box>
          <IconButton onClick={onClose}><Close /></IconButton>
        </Stack>

        {isLoading ? (
          <Stack spacing={2}>
            <Skeleton variant="rounded" height={80} />
            <Skeleton variant="rounded" height={200} />
          </Stack>
        ) : (
          <>
            <Grid container spacing={1.5} mb={2}>
              <Grid item xs={12} sm={4}>
                <StatCard
                  title="Referral code"
                  value={data?.referralCode || partner?.referralCode || 'Generating…'}
                  icon={<CardGiftcard fontSize="small" />}
                  color="#7c3aed"
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <StatCard
                  title="Total referred"
                  value={data?.totalReferred ?? 0}
                  icon={<People fontSize="small" />}
                  color="#2563eb"
                />
              </Grid>
              <Grid item xs={6} sm={4}>
                <StatCard
                  title="Total earnings"
                  value={`${formatMoney((data?.totalEarnings ?? 0))}`}
                  icon={<Payments fontSize="small" />}
                  color="#059669"
                />
              </Grid>
            </Grid>

            {(data?.referralCode || partner?.referralCode) && (
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Chip
                  label={data?.referralCode || partner?.referralCode}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 700, letterSpacing: 1 }}
                />
                <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
                  <Button size="small" variant="outlined" startIcon={<ContentCopy />} onClick={handleCopy}>
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </Tooltip>
                <Button size="small" onClick={() => refetch()}>Refresh</Button>
              </Stack>
            )}

            <Alert severity="info" icon={<PersonAdd />} sx={{ mb: 2 }}>
              Users who sign up with this partner&apos;s referral code appear below. Bonus is credited per referral policy.
            </Alert>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 1.5, minHeight: 36 }}>
              <Tab label={`Referred users (${data?.referredUsers?.length ?? 0})`} sx={{ minHeight: 36 }} />
              <Tab label={`History (${data?.history?.length ?? 0})`} sx={{ minHeight: 36 }} />
            </Tabs>

            <Box sx={{ flex: 1, minHeight: 0 }}>
              {tab === 0 && (
                <SerialNoDataGrid
                  rows={data?.referredUsers || []}
                  columns={referredColumns}
                  getRowId={(r) => r._id as string}
                  autoHeight
                  hideFooter={(data?.referredUsers?.length || 0) <= 10}
                  sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
                />
              )}
              {tab === 1 && (
                <SerialNoDataGrid
                  rows={data?.history || []}
                  columns={historyColumns}
                  getRowId={(r) => (r as { _id: string })._id}
                  autoHeight
                  hideFooter={(data?.history?.length || 0) <= 10}
                  sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
                />
              )}
              {tab === 0 && !(data?.referredUsers?.length) && (
                <Typography color="text.secondary" textAlign="center" py={4}>No referred users yet.</Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
